import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useId, useRef, useState } from 'react';

import {
  BackupBlockedReason,
  BackupCandidate,
  BackupStatus,
} from '../api/backupInvites';
import {
  useBackupInviteActions,
  useBackupInvites,
} from '../hooks/useBackupInvites';

import useDashboardToast from '../hooks/useDashboardToast';

import Avatar from './Avatar';

import {
  backupSettingsClient,
  levelValue,
} from '@/routes/Agency/BackupInvites/api';
import BackupsSetting from '@/routes/Agency/BackupInvites/components/BackupsSetting';
import {
  useBackupChoice,
  useBackupSave,
} from '@/routes/Agency/BackupInvites/hooks';

/**
 * "Invite backups" for one shift (GRAVY-10898), opened from the Shift Detail
 * panel footer. No ui-prototype exists for it; it is built from the dashboard's
 * own modal shell, warning banner and inline confirm.
 *
 * Both actions here have effects well beyond the click, so each goes through a
 * yellow confirm that says what will happen:
 *
 * - Sending texts and pushes the ticked workers now. One who accepts a FULL
 *   shift goes on standby, and the earliest standby is HIRED AUTOMATICALLY when
 *   a spot opens before the start — nobody in ops is asked again.
 * - The switch is GLOBAL (Automation::Flags::BACKUP_INVITES), a master
 *   switch: on, auto backups (invites, the 3-hour auto-invite, standby and
 *   auto-hire from standby) run only where a client is opted in and no order
 *   or job is switched off; clients are off until enabled. Off, it stops all of
 *   it everywhere. It is not a setting of the shift in the panel.
 *
 * Under the global switch sits THIS job's own setting (job ?? order ?? client,
 * see BackupInvites/api). It is a different thing from the switch: off here
 * blocks invites for this job only (`off_for_*`), and the switch must be on
 * as well for anything to run. Its changes go through the same kind of warning.
 *
 * The server stays the authority: `can_invite` / `blocked_reason` mirror the
 * POST's own refusals, and a refusal that gets through anyway is shown inline.
 */

type Props = {
  jobId: number;
  onClose: () => void;
};

const STATUS_LABELS: Record<BackupStatus, string> = {
  invited: 'Invited',
  on_standby: 'On standby',
  hired: 'Hired',
  declined: 'Declined',
  expired: 'Expired',
};

const BLOCKED_LABELS: Record<NonNullable<BackupBlockedReason>, string> = {
  switch_off: 'Auto backups are switched off, so none can be sent.',
  job_cancelled: 'This shift is cancelled, so backups can’t be invited.',
  job_started: 'This shift has started, so backups can’t be invited.',
  off_for_job: 'Auto backups are off for this job.',
  off_for_order: 'Auto backups are off for this order.',
  off_for_client: 'Auto backups are off for this client.',
};

// Off at some level: the admin can turn it on for this one job from here.
const OFF_REASONS = new Set<BackupBlockedReason>([
  'off_for_job',
  'off_for_order',
  'off_for_client',
]);

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

const tierLabel = (tier?: string | null) =>
  tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : null;

const distanceLabel = (mi?: number | null) =>
  mi == null ? null : `${mi.toFixed(1)} mi`;

const InviteBackupsModal = ({ jobId, onClose }: Props) => {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const confirmCancelRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<Element | null>(
    typeof document === 'undefined' ? null : document.activeElement,
  );
  const { data, isLoading, isError } = useBackupInvites(jobId);
  const { send, setSwitch, pending, error } = useBackupInviteActions(jobId);
  const { notify } = useDashboardToast();
  // This job's own backups setting (job ?? order ?? client). Saving refetches
  // this modal's data and the calendar, which carry `blocked_reason` and the
  // cards' "Backups off".
  const jobSetting = useBackupSave(
    (enabled) => backupSettingsClient.setJob(jobId, enabled),
    'Could not save auto backups for this job. Please try again.',
    (saved, enabled) => {
      const on = saved.enabled ?? saved.order_enabled ?? saved.client_enabled;
      notify({
        key: `backup_setting:${jobId}:${Date.now()}`,
        tone: 'info',
        title: `Auto backups ${on ? 'on' : 'off'} for this job`,
        message:
          enabled === null
            ? 'It follows the order’s setting again.'
            : on
              ? 'GravyWork invites backups at the 3-hour check, and standby is hired when a spot opens.'
              : 'No automatic backup invites, and nobody will be hired from standby on this job.',
      });
    },
  );
  const jobChooser = useBackupChoice(jobSetting.save);
  const jobLevel = data?.setting
    ? levelValue('job', {
        job: data.setting.job,
        order: data.setting.order,
        client: data.setting.client,
      })
    : null;

  // Who the admin UNticked, rather than who is ticked: every candidate is
  // selected by default, including ones that arrive on a refetch (turning the
  // switch on is what fills the list).
  const [unticked, setUnticked] = useState<Set<number>>(() => new Set());
  const [confirming, setConfirming] = useState<
    null | 'send' | 'switch_on' | 'switch_off'
  >(null);

  const candidates: BackupCandidate[] = data?.candidates ?? [];
  const selected = candidates
    .map((c) => c.worker_id)
    .filter((id) => !unticked.has(id));
  const allSelected =
    candidates.length > 0 && selected.length === candidates.length;
  const canSend = !!data?.can_invite && selected.length > 0 && pending == null;

  useEffect(() => {
    closeRef.current?.focus();
    const opener = openerRef.current as HTMLElement | null;
    return () => opener?.focus?.();
  }, []);

  // A confirm takes focus on Cancel, so Enter on a just-opened warning never
  // sends or flips the switch by accident.
  useEffect(() => {
    if (confirming) confirmCancelRef.current?.focus();
  }, [confirming]);

  // Capture phase and stopPropagation, like InvitedWorkersModal: the Shift
  // Detail panel underneath also closes on Escape. An open confirm is closed
  // first, so one Escape never dismisses the warning AND the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (confirming) setConfirming(null);
      else onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [confirming, onClose]);

  const toggle = (id: number) =>
    setUnticked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setUnticked(
      allSelected ? new Set(candidates.map((c) => c.worker_id)) : new Set(),
    );

  const confirmSend = async () => {
    setConfirming(null);
    const result = await send(selected);
    if (result) onClose();
  };

  const confirmSwitch = async (enabled: boolean) => {
    setConfirming(null);
    await setSwitch(enabled);
  };

  const n = selected.length;

  const switchSection = data ? (
    data.switch_on ? (
      <div
        className="dashv2-backup-switchline"
        data-testid="backup-invites-switch-on"
      >
        <span>Auto backups master switch is on</span>
        <button
          type="button"
          className="dashv2-backup-quiet"
          data-testid="backup-invites-turn-off"
          disabled={pending != null}
          onClick={() => setConfirming('switch_off')}
        >
          Turn off
        </button>
      </div>
    ) : (
      <div
        className="dashv2-backup-banner"
        data-testid="backup-invites-switch-off"
      >
        <ExclamationTriangleIcon />
        <span>Auto backups are switched off everywhere.</span>
        <button
          type="button"
          className="dashv2-backup-banner-btn"
          data-testid="backup-invites-turn-on"
          disabled={pending != null}
          onClick={() => setConfirming('switch_on')}
        >
          {pending === 'switch' ? 'Turning on…' : 'Turn on'}
        </button>
      </div>
    )
  ) : null;

  const switchConfirm =
    confirming === 'switch_on' || confirming === 'switch_off' ? (
      <div
        className="dashv2-review-confirm dashv2-backup-confirm"
        role="alertdialog"
        aria-label={
          confirming === 'switch_on'
            ? 'Turn on the auto backups master switch'
            : 'Turn off auto backups everywhere'
        }
        data-testid="backup-invites-confirm-switch"
      >
        {confirming === 'switch_on' ? (
          <>
            <p className="dashv2-backup-confirm-title">
              Turn on the auto backups master switch?
            </p>
            {/* The global switch is a master switch, not "every shift": clients
                are opt-in (customers.backup_invites_enabled defaults false), so
                auto backups then run only where a client, order or job is on. */}
            <ul>
              <li>
                Auto backups then run for the clients, orders and jobs where
                they are turned on. Clients are off until someone enables them.
              </li>
              <li>
                On those jobs GravyWork invites backups automatically at the
                3-hour check when hired workers haven&apos;t confirmed, and ops
                can invite by hand.
              </li>
              <li>
                Workers who accept a shift that is already full go on standby.
                When a spot opens, the earliest standby is hired automatically,
                without asking ops.
              </li>
              <li>The worker app shows standby screens.</li>
            </ul>
          </>
        ) : (
          <p>
            Turn off auto backups everywhere? No backup invites go out and
            nobody is hired from standby on any shift while it&apos;s off.
          </p>
        )}
        <div className="dashv2-review-confirm-actions">
          <button
            ref={confirmCancelRef}
            type="button"
            className="dashv2-review-confirm-cancel"
            data-testid="backup-invites-confirm-switch-cancel"
            onClick={() => setConfirming(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="dashv2-review-confirm-go"
            data-testid="backup-invites-confirm-switch-go"
            onClick={() => confirmSwitch(confirming === 'switch_on')}
          >
            {confirming === 'switch_on'
              ? 'Turn on master switch'
              : 'Turn off everywhere'}
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div
      className="dashv2-actmodal-overlay dashv2-backupmodal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="dashv2-actmodal dashv2-backupmodal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="backup-invites-modal"
      >
        <div className="dashv2-actmodal-head">
          <h2 id={titleId} className="dashv2-actmodal-title">
            Invite backups
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="dashv2-panel-close"
            aria-label="Close invite backups"
            onClick={onClose}
          >
            <XMarkIcon />
          </button>
        </div>

        <div className="dashv2-actmodal-body dashv2-backupmodal-body">
          {isLoading && !data ? (
            <p className="dashv2-actmodal-state">Loading…</p>
          ) : isError || !data ? (
            <p className="dashv2-actmodal-state" role="alert">
              Unable to load backups for this shift.
            </p>
          ) : (
            <>
              {switchSection}
              {switchConfirm}

              {jobLevel && (
                <div className="dashv2-backupmodal-setting">
                  <BackupsSetting
                    level="job"
                    label="Auto backups for this job"
                    {...jobLevel}
                    saving={jobSetting.saving}
                    error={jobSetting.error}
                    chooser={jobChooser}
                    testId="backup-invites-job-setting"
                  />
                </div>
              )}

              {!data.can_invite && data.blocked_reason && (
                <p
                  className="dashv2-backup-blocked"
                  data-testid="backup-invites-blocked"
                  data-reason={data.blocked_reason}
                >
                  {BLOCKED_LABELS[data.blocked_reason]}
                  {jobLevel &&
                    !jobLevel.value &&
                    OFF_REASONS.has(data.blocked_reason) && (
                      <button
                        type="button"
                        className="dashv2-backup-quiet dashv2-backup-blocked-on"
                        data-testid="backup-invites-blocked-turn-on"
                        disabled={
                          jobSetting.saving || jobChooser.choice != null
                        }
                        onClick={() => jobChooser.request(true, true, false)}
                      >
                        Turn on for this job
                      </button>
                    )}
                </p>
              )}

              {data.backups.length > 0 && (
                <section className="dashv2-backup-section">
                  <h3 className="dashv2-backup-sectiontitle">
                    Already backups ({data.backups.length})
                  </h3>
                  {data.backups.map((b) => (
                    <div
                      key={b.worker_id}
                      className="dashv2-backup-row"
                      data-testid={`backup-invites-backup-${b.worker_id}`}
                    >
                      <Avatar name={b.name} />
                      <span className="dashv2-backup-name">
                        {b.name || 'Unknown worker'}
                      </span>
                      <span
                        className={`dashv2-invitedstatus is-${b.status}`}
                        data-status={b.status}
                      >
                        {STATUS_LABELS[b.status]}
                      </span>
                    </div>
                  ))}
                </section>
              )}

              <section className="dashv2-backup-section">
                <div className="dashv2-backup-sectionhead">
                  <h3 className="dashv2-backup-sectiontitle">
                    Suggested backups ({candidates.length})
                  </h3>
                  {candidates.length > 0 && (
                    <label className="dashv2-backup-selectall">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        data-testid="backup-invites-select-all"
                      />
                      Select all
                    </label>
                  )}
                </div>
                {candidates.length === 0 ? (
                  <p
                    className="dashv2-panel-empty"
                    data-testid="backup-invites-empty"
                  >
                    {data.switch_on
                      ? 'Nobody else can be invited as a backup right now.'
                      : 'Turn auto backups on to see who could be invited.'}
                  </p>
                ) : (
                  // Best match first, as the server ranked them.
                  candidates.map((c) => {
                    const meta = [
                      tierLabel(c.tier),
                      distanceLabel(c.distance_mi),
                    ]
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <label
                        key={c.worker_id}
                        className="dashv2-backup-row is-candidate"
                        data-testid={`backup-invites-candidate-${c.worker_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!unticked.has(c.worker_id)}
                          onChange={() => toggle(c.worker_id)}
                          data-testid={`backup-invites-candidate-${c.worker_id}-checkbox`}
                        />
                        <Avatar name={c.name} />
                        <span className="dashv2-backup-namecol">
                          <span className="dashv2-backup-name">
                            {c.name || 'Unknown worker'}
                          </span>
                          {meta && (
                            <span className="dashv2-backup-meta">{meta}</span>
                          )}
                        </span>
                      </label>
                    );
                  })
                )}
              </section>
            </>
          )}
        </div>

        {error && (
          <p
            className="dashv2-backup-error"
            role="alert"
            data-testid="backup-invites-error"
          >
            {error}
          </p>
        )}

        {confirming === 'send' && (
          <div
            className="dashv2-review-confirm dashv2-backup-confirm"
            role="alertdialog"
            aria-label={`Send backup invites to ${plural(n, 'worker', 'workers')}`}
            data-testid="backup-invites-confirm-send"
          >
            <p className="dashv2-backup-confirm-title">
              Send backup invites to {plural(n, 'worker', 'workers')}?
            </p>
            <ul>
              <li>
                Each gets a text and a push now, held until 8 AM if it&apos;s
                night where the shift is.
              </li>
              <li>
                Anyone who accepts while the shift is full goes on standby.
              </li>
              <li>
                When a spot opens before the start, the earliest standby is
                hired automatically, without asking you.
              </li>
              <li>
                At short notice they must reply 1 within 15 minutes, or the next
                standby is tried.
              </li>
            </ul>
            <div className="dashv2-review-confirm-actions">
              <button
                ref={confirmCancelRef}
                type="button"
                className="dashv2-review-confirm-cancel"
                data-testid="backup-invites-confirm-send-cancel"
                onClick={() => setConfirming(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dashv2-review-confirm-go"
                data-testid="backup-invites-confirm-send-go"
                onClick={confirmSend}
              >
                Send {plural(n, 'invite', 'invites')}
              </button>
            </div>
          </div>
        )}

        <div className="dashv2-actmodal-foot dashv2-backupmodal-foot">
          <button
            type="button"
            className="dashv2-panel-btn is-secondary"
            data-testid="backup-invites-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="dashv2-panel-btn is-filled"
            data-testid="backup-invites-send"
            disabled={!canSend || confirming === 'send'}
            onClick={() => setConfirming('send')}
          >
            {pending === 'send'
              ? 'Sending…'
              : `Invite ${plural(n, 'backup', 'backups')}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteBackupsModal;
